import React, { useState, useEffect } from 'react';
import { theme } from '../../theme';
import { CATEGORIES, REQUEST_STATUSES } from '../../constants';
import { formatTimeAgo, formatPrice } from '../../utils/formatters';
import { useApi } from '../../hooks/useApi';

export function BrowseScreen({ onNavigate }) {
  const api = useApi();
  const [tab, setTab] = useState('requests'); // 'requests' | 'makers'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [requests, setRequests] = useState([]);
  const [makers, setMakers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const params = {};
        if (categoryFilter !== 'all') params.category = categoryFilter;

        if (tab === 'requests') {
          const res = await api.requests.list(params);
          if (!cancelled) setRequests(res.data || []);
        } else {
          const res = await api.makers.list(params);
          if (!cancelled) setMakers(res.data || []);
        }
      } catch (err) {
        console.error('Browse fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [tab, categoryFilter]);

  return (
    <div style={{ padding: '16px' }}>
      {/* Tab Switcher */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        background: theme.bgCard,
        borderRadius: theme.radius,
        padding: '4px',
      }}>
        {[
          { id: 'requests', label: 'Beiðnir' },
          { id: 'makers', label: 'Makers' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: theme.radiusSm,
              border: 'none',
              background: tab === t.id ? theme.accent : 'transparent',
              color: tab === t.id ? '#000' : theme.textSecondary,
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Category Filter */}
      <div style={{
        display: 'flex',
        gap: '8px',
        overflowX: 'auto',
        paddingBottom: '12px',
        marginBottom: '16px',
      }}>
        <button
          onClick={() => setCategoryFilter('all')}
          style={{
            flexShrink: 0,
            padding: '6px 14px',
            borderRadius: '20px',
            border: `1px solid ${categoryFilter === 'all' ? theme.accent : theme.border}`,
            background: categoryFilter === 'all' ? theme.accentBg : 'transparent',
            color: categoryFilter === 'all' ? theme.accent : theme.textSecondary,
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Allt
        </button>
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setCategoryFilter(cat.id)}
            style={{
              flexShrink: 0,
              padding: '6px 14px',
              borderRadius: '20px',
              border: `1px solid ${categoryFilter === cat.id ? theme.accent : theme.border}`,
              background: categoryFilter === cat.id ? theme.accentBg : 'transparent',
              color: categoryFilter === cat.id ? theme.accent : theme.textSecondary,
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: theme.textMuted }}>
          Hleð...
        </div>
      ) : tab === 'requests' ? (
        requests.length === 0 ? (
          <div style={{ color: theme.textSecondary, textAlign: 'center', padding: '40px' }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>📋</p>
            <p style={{ fontSize: '15px', fontWeight: 600, color: theme.text }}>Engar beiðnir enn</p>
            <p style={{ fontSize: '13px', marginTop: '4px' }}>Vertu fyrstur til að senda beiðni!</p>
            <button
              onClick={() => onNavigate('new-request')}
              style={{
                marginTop: '16px',
                background: theme.accent,
                color: '#000',
                border: 'none',
                padding: '10px 24px',
                borderRadius: theme.radiusSm,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Senda beiðni
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {requests.map(req => {
              const cat = CATEGORIES.find(c => c.id === req.category);
              const status = REQUEST_STATUSES[req.status] || REQUEST_STATUSES.open;
              return (
                <button
                  key={req.id}
                  onClick={() => onNavigate('request-detail', req.id)}
                  style={{
                    background: theme.bgCard,
                    border: `1px solid ${theme.border}`,
                    borderRadius: theme.radius,
                    padding: '14px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{
                      fontSize: '12px',
                      padding: '3px 8px',
                      borderRadius: '10px',
                      background: `${status.color}20`,
                      color: status.color,
                      fontWeight: 600,
                    }}>
                      {status.label}
                    </span>
                    <span style={{ fontSize: '11px', color: theme.textMuted }}>
                      {formatTimeAgo(req.created_at)}
                    </span>
                  </div>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: theme.text, marginBottom: '4px' }}>
                    {req.title}
                  </h3>
                  <p style={{ fontSize: '13px', color: theme.textSecondary, lineHeight: 1.4 }}>
                    {req.description?.substring(0, 100)}{req.description?.length > 100 ? '...' : ''}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                    <span style={{ fontSize: '12px', color: theme.categories[req.category] || theme.textMuted }}>
                      {cat?.icon} {cat?.label}
                    </span>
                    <span style={{ fontSize: '12px', color: theme.textMuted }}>
                      {req.quantity} stk. {req.quote_count > 0 ? `· ${req.quote_count} tilboð` : ''}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : (
        makers.length === 0 ? (
          <div style={{ color: theme.textSecondary, textAlign: 'center', padding: '40px' }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>🔧</p>
            <p style={{ fontSize: '15px', fontWeight: 600, color: theme.text }}>Engir makers skráðir enn</p>
            <p style={{ fontSize: '13px', marginTop: '4px' }}>Ertu maker? Skráðu þig!</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {makers.map(maker => (
              <button
                key={maker.id}
                onClick={() => onNavigate('maker-profile', maker.id)}
                style={{
                  background: theme.bgCard,
                  border: `1px solid ${theme.border}`,
                  borderRadius: theme.radius,
                  padding: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                }}
              >
                <div style={{ fontSize: '36px', flexShrink: 0 }}>{maker.avatar || '🔧'}</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: theme.text, marginBottom: '2px' }}>
                    {maker.name}
                  </h3>
                  {maker.location && (
                    <p style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>
                      📍 {maker.location}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: theme.accent, fontWeight: 600 }}>
                      {'★'.repeat(Math.round(maker.rating || 0))} {(maker.rating || 0).toFixed(1)}
                    </span>
                    <span style={{ fontSize: '12px', color: theme.textMuted }}>
                      {maker.service_count || 0} þjónustur · {maker.completed_orders || 0} pantanir
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}
